import { decamelizeKeys } from 'humps'
import _ from 'lodash/fp'
import gql from 'graphql-tag'
import { all, call, put, select, take, takeLatest } from 'redux-saga/effects'

import { actions, types } from '../reducers/holesTees'
import { selectors as clubsSelectors } from '../reducers/clubs'
import { actions as coursesActions } from '../reducers/courses'
import { actions as teesActions } from '../reducers/tees'

import { apolloClient } from '@fe/business/src/apollo'

//  ███████╗███████╗███████╗███████╗ ██████╗████████╗███████╗
//  ██╔════╝██╔════╝██╔════╝██╔════╝██╔════╝╚══██╔══╝██╔════╝
//  █████╗  █████╗  █████╗  █████╗  ██║        ██║   ███████╗
//  ██╔══╝  ██╔══╝  ██╔══╝  ██╔══╝  ██║        ██║   ╚════██║
//  ███████╗██║     ██║     ███████╗╚██████╗   ██║   ███████║
//  ╚══════╝╚═╝     ╚═╝     ╚══════╝ ╚═════╝   ╚═╝   ╚══════╝

const CLUB_HOLES_TEES_QUERY = gql`
  query ClubHolesTees($clubId: ID!) {
    clubHolesTees(clubId: $clubId) {
      id
      holeId
      teeId
      distance
      createdAt
      updatedAt
    }
  }
`

export function * loadHolesTeesEffect () {
  try {
    const clubId = yield select(clubsSelectors.getActive)
    const response = yield call([apolloClient, apolloClient.query], {
      query: CLUB_HOLES_TEES_QUERY,
      variables: { clubId },
      fetchPolicy: 'network-only'
    })
    const holesTees = _.getOr([], 'data.clubHolesTees', response)
    return decamelizeKeys(holesTees)
  } catch (error) {
    return { error }
  }
}

const UPDATE_HOLE = gql`
  mutation UpdateHole($input: UpdateHoleInput!) {
    CoursesMutation {
      updateHole(input: $input) {
        id
        guideline
        par
        handicap
        greenDataPosition
      }
    }
  }
`

const UPDATE_HOLES = gql`
  mutation UpdateHoles($input: UpdateHolesInput!) {
    CoursesMutation {
      updateHoles(input: $input) {
        id
        guideline
        par
        handicap
        greenDataPosition
      }
    }
  }
`

const toHoleInput = (hole) => ({
  id: _.get('id', hole),
  guideline: _.get('guideline', hole),
  par: _.get('par', hole),
  handicap: _.get('handicap', hole),
  greenDataPosition: _.has('greenDataPosition', hole)
    ? _.get('greenDataPosition', hole)
    : _.get('green_data_position', hole),
  distances: _.map((dist) => ({
    teeId: _.get('tee_id', dist),
    distance: _.get('distance', dist)
  }), _.get('distances', hole))
})

export function * saveHolesTeesEffect (hole) {
  const clubId = yield select(clubsSelectors.getActive)

  try {
    const response = yield call([apolloClient, apolloClient.mutate], {
      mutation: UPDATE_HOLE,
      variables: { input: { clubId, ...toHoleInput(hole) } }
    })
    return decamelizeKeys(_.get('data.CoursesMutation.updateHole', response))
  } catch (error) {
    return { error }
  }
}

export function * saveAllHolesTeesEffect (holes) {
  const clubId = yield select(clubsSelectors.getActive)

  try {
    const response = yield call([apolloClient, apolloClient.mutate], {
      mutation: UPDATE_HOLES,
      variables: { input: { clubId, holes: _.map(toHoleInput, holes) } }
    })
    return decamelizeKeys(_.getOr([], 'data.CoursesMutation.updateHoles', response))
  } catch (error) {
    return { error }
  }
}

//  ███████╗ █████╗  ██████╗  █████╗ ███████╗
//  ██╔════╝██╔══██╗██╔════╝ ██╔══██╗██╔════╝
//  ███████╗███████║██║  ███╗███████║███████╗
//  ╚════██║██╔══██║██║   ██║██╔══██║╚════██║
//  ███████║██║  ██║╚██████╔╝██║  ██║███████║
//  ╚══════╝╚═╝  ╚═╝ ╚═════╝ ╚═╝  ╚═╝╚══════╝

export function * loadHolesTeesFlow ({ payload }) {
  const result = yield call(loadHolesTeesEffect)
  yield put(actions.loadRequestDone())

  if (result.error) {
    yield put(actions.loadError(result))
  } else {
    yield put(actions.loadSuccess(result))
  }
}

export function * saveHolesTeesFlow ({ payload }) {
  const hole = _.flow(
    decamelizeKeys,
    _.set('green_data_position', _.has('greenDataPosition', payload)
      ? _.get('greenDataPosition', payload)
      : _.get('green_data_position', payload)),
    _.omit([
      'created_at',
      'description',
      'name',
      'order',
      'tees',
      'updated_at'
    ]),
    _.update('distances', _.map(_.omit(['id', 'created_at', 'updated_at'])))
  )(payload)
  const result = yield call(saveHolesTeesEffect, hole)

  if (result.error) {
    yield put(actions.saveError(result))
  } else {
    yield put(actions.saveSuccess())
    yield put(coursesActions.loadRequest())
  }
  yield put(actions.loadRequest())
  yield take([types.LOAD_ERROR, types.LOAD_SUCCESS])
  yield put(actions.saveDone())
  yield put(teesActions.loadRequest())
}

export function * saveAllHolesTeesFlow ({ payload }) {
  const holes = _.map(
    _.flow(
      _.omit(['created_at', 'description', 'name', 'order', 'tees', 'updated_at']),
      _.update('distances', _.map(_.omit(['id', 'created_at', 'updated_at'])))
    ),
    payload
  )
  const result = yield call(saveAllHolesTeesEffect, holes)

  if (result.error) {
    yield put(actions.saveAllError(result))
  } else {
    yield put(actions.saveAllSuccess())
  }
  yield put(actions.loadRequest())
  yield take([types.LOAD_ERROR, types.LOAD_SUCCESS])
  yield put(actions.saveAllDone())
  yield put(teesActions.loadRequest())
}

export default function * () {
  return yield all([
    takeLatest(types.LOAD_REQUEST, loadHolesTeesFlow),
    takeLatest(types.SAVE, saveHolesTeesFlow),
    takeLatest(types.SAVE_ALL, saveAllHolesTeesFlow)
  ])
}
